<script lang="ts" setup>
import api from "@/api";
import type { TCity, TLanguage } from "@/assets/js/globalTypes";
import { debounce, FILE_PICKER_PT, useIsMobile } from "@/assets/js/helpers";
import { useProfileStore } from "@/store/profile";
import type { SelectChangeEvent, SelectFilterEvent } from "primevue/select";
import { $t } from "@/i18n";
import ProfileImage from "@/assets/image/dai-dai-placeholder-profile.png";

definePage({
	name: "teacher.profile.general-info",
	meta: {
		returnToTop: true,
	},
});

const cities = ref<TCity[]>([]);

const citiesLoading = ref(false);
const getCitiesDebounced = debounce(getCities, 500, false);
async function getCities() {
	citiesLoading.value = true;
	try {
		const res = await api.getCities({ cit_name: citySearch.value });
		cities.value = res.data.data || [];
	} catch (error) {
		console.error(error);
		cities.value = [];
	} finally {
		citiesLoading.value = false;
	}
}

const citiesOptions = computed(() => {
	const res = [...cities.value];
	const uniqueCities = new Map();
	for (const city of [...(profileStore.profileChanges.cities || []), ...res]) {
		uniqueCities.set(city.cit_id, city);
	}
	return Array.from(uniqueCities.values());
	// return cities.value;
});

const citySearch = ref("");

const languages = ref<TLanguage[]>([]);

function handleLanguageSelect(e: SelectChangeEvent) {
	const selectedLanguageObjects = languages.value.filter((lang) =>
		e.value.includes(lang.lng_code),
	);
	profileStore.profileChanges.languages = selectedLanguageObjects;
}

function handleCitySelect(e: SelectChangeEvent) {
	const selectedCities = citiesOptions.value.filter((city) =>
		e.value.includes(city.cit_id),
	);
	profileStore.profileChanges.cities = selectedCities;
}

async function getTeachingLanguages() {
	try {
		const res = await api.getTeachingLanguages();
		languages.value = res.data.data;
		console.log(languages.value);
	} catch (error) {
		console.error(error);
	}
}

const getMessage = computed(() => $t("app.base.pleaseSearchCity"));

const profileStore = useProfileStore();

function updateImage(e) {
	if (e.files.length > 1) {
		return;
	}
	profileStore.setNewImage(e.files[0]);
}

function handleDescriptionInput(e: Event) {
	const target = e.target as HTMLTextAreaElement;
	if (target.value.length > 255) {
		target.value = profileStore.profileChanges.usr_description;
		return;
	}
	profileStore.profileChanges.usr_description = target.value;
}

function handleCitySearch(e: SelectFilterEvent) {
	citySearch.value = e.value;
	getCitiesDebounced();
}

const isMobile = useIsMobile();

const hasPendingValidation = computed(() => {
	const pendingColumns = profileStore.profilePending?.columns ?? [];
	const keysToCheck = [
		"usr_fullname",
		"usr_email",
		"usr_title",
		"usr_avatar",
	] as const;

	return keysToCheck.some((key) => pendingColumns.includes(key));
});

onMounted(() => {
	getTeachingLanguages();
});
</script>
<template>
	<div>
		<div
			v-if="isMobile"
			class="sub-title-profile-page"
		>
			{{ $t("app.base.step") }} 1 |
			{{ $t("app.teacher.profileStepper.general") }}
			<span v-tooltip.bottom="$t('app.teacher.profile.pendingValidation')">
				<i-material-symbols:alarm-outline-rounded v-if="hasPendingValidation" />
			</span>
		</div>
		<info-icon
			class="help-icon-profile-page"
			:text="$t('app.teacher.profile.generalInfoHelperText')"
		/>
		<div
			v-if="isMobile"
			class="teacher-profile-fake-circle"
		></div>
		<div class="content-wrap">
			<div class="image-wrap">
				<label class="sub-title">{{ $t("app.base.profilePicture") }} *</label>
				<p-image
					v-tooltip.bottom="
						profileStore.isFieldPending('columns', 'usr_avatar')
							? $t('app.teacher.profile.pendingValidation')
							: ''
					"
					style="height: 150px; width: 150px"
					shape="circle"
					:preview="profileStore.usrImageChange !== ProfileImage"
					:src="profileStore.usrImageChange"
					:pt="{
						root: {
							class: 'general-info-image-fix',
							style: 'overflow: hidden; border-radius: 50%;',
						},
						image: {
							style: 'width: 100%;',
						},
						rotateRightButton: {
							style: 'display: none',
						},
						rotateLeftButton: {
							style: 'display: none',
						},
					}"
					:class="{
						'pending-validation': profileStore.isFieldPending(
							'columns',
							'usr_avatar',
						),
					}"
				></p-image>

				<p-file-upload
					ref="fileUpload"
					mode="basic"
					accept="image/*"
					auto
					custom-upload
					style="white-space: nowrap"
					class="general-info-file-upload-fix"
					:invalid-file-size-message="$t('app.files.maxSizeMessage')"
					:invalid-file-type-message="$t('app.files.invalidTypeMessage')"
					:invalid-file-limit-message="$t('app.files.maxFilesMessage')"
					:choose-label="
						$t('app.base.upload', {
							item: $t('app.base.image', 2).toLowerCase(),
						})
					"
					:max-file-size="5243000"
					:pt="FILE_PICKER_PT"
					@select="updateImage"
				></p-file-upload>
			</div>
			<p-fluid class="basic-wrap">
				<div class="sub-title">
					<label>
						{{ $t("app.teacher.profile.basic") }}
					</label>
					<small>* - {{ $t("app.base.requiredFields") }}</small>
					<span
						v-tooltip.bottom="$t('app.teacher.profile.pendingValidation')"
						class="panding-validation-icon"
					>
						<i-material-symbols:alarm-outline-rounded
							v-if="hasPendingValidation && !isMobile"
						/>
					</span>
				</div>
				<div class="item-group">
					<div class="item">
						<p-float-label
							:pt="{
								root: {
									class: 'my-profile-float-label-root',
								},
							}"
						>
							<p-input-text
								id="profile-usr_fullname"
								v-model="profileStore.profileChanges.usr_fullname"
								v-tooltip.bottom="
									profileStore.isFieldPending('columns', 'usr_fullname')
										? $t('app.teacher.profile.pendingValidation')
										: ''
								"
								:min="1"
								:disabled="!profileStore.finishedProfileSetup"
								:class="{
									'pending-validation': profileStore.isFieldPending(
										'columns',
										'usr_fullname',
									),
								}"
								:pt="{
									root: {
										class: 'my-profile-input-root',
									},
								}"
							></p-input-text>
							<label for="profile-usr_fullname">
								{{ $t("app.base.name") }} *
							</label>
						</p-float-label>
						<p-float-label
							:pt="{
								root: {
									class: 'my-profile-float-label-root',
								},
							}"
						>
							<p-input-text
								id="profile-usr_email"
								v-model="profileStore.profileChanges.usr_email"
								v-tooltip.bottom="
									profileStore.isFieldPending('columns', 'usr_email')
										? $t('app.teacher.profile.pendingValidation')
										: ''
								"
								disabled
								:class="{
									'pending-validation': profileStore.isFieldPending(
										'columns',
										'usr_email',
									),
								}"
								:pt="{
									root: {
										class: 'my-profile-input-root',
									},
								}"
							></p-input-text>
							<label for="profile-usr_phone">
								{{ $t("app.auth.email") }} *
							</label>
						</p-float-label>
					</div>
					<div class="item">
						<p-float-label
							:pt="{
								root: {
									class: 'my-profile-float-label-root',
								},
							}"
						>
							<p-input-text
								id="profile-usr_title"
								v-model="profileStore.profileChanges.usr_title"
								v-tooltip.bottom="
									profileStore.isFieldPending('columns', 'usr_title')
										? $t('app.teacher.profile.pendingValidation')
										: ''
								"
								:disabled="!profileStore.finishedProfileSetup"
								:class="{
									'pending-validation': profileStore.isFieldPending(
										'columns',
										'usr_title',
									),
								}"
								:pt="{
									root: {
										class: 'my-profile-input-root',
									},
								}"
							></p-input-text>
							<label for="profile-usr_title">
								{{ $t("app.base.title") }} *
							</label>
						</p-float-label>
						<p-float-label
							:pt="{
								root: {
									class: 'my-profile-float-label-root',
								},
							}"
						>
							<p-input-text
								id="profile-usr_phone"
								v-model="profileStore.profileChanges.usr_phone"
								:disabled="!profileStore.finishedProfileSetup"
								:pt="{
									root: {
										class: 'my-profile-input-root',
									},
								}"
							></p-input-text>
							<label for="profile-usr_phone">
								{{ $t("app.base.phone") }} *
							</label>
						</p-float-label>
					</div>
				</div>
				<label class="sub-title">
					{{ $t("app.teacher.profile.lang-loc") }}
				</label>
				<div class="item-group">
					<div class="item">
						<p-multi-select
							id="profile-preffered-cities"
							v-model="profileStore.cityChangesIds"
							:options="cities"
							option-label="cit_name"
							:loading="citiesLoading"
							filter-mode="lenient"
							auto-filter-focus
							:show-toggle-all="false"
							option-value="cit_id"
							filter
							:empty-selection-message="$t('app.base.noItem')"
							:empty-filter-message="$t('app.base.noResults')"
							:empty-message="$t('app.base.noAvailableOptions')"
							:placeholder="$t('app.teacher.prefferedCities') + ' *'"
							:selected-items-label="$t('app.teacher.prefferedCities') + ' *'"
							:max-selected-labels="0"
							fluid
							filter-match-mode="startsWith"
							:pt="{
								root: {
									class: 'my-profile-input-root',
								},
							}"
							@filter="handleCitySearch"
							@change="handleCitySelect"
						>
							<template #header>
								<div class="dropdown-header-text">
									{{ getMessage }}
								</div>
							</template>
						</p-multi-select>

						<div class="chip-wrap">
							<p-chip
								v-for="city in profileStore.profileChanges.cities"
								:key="city.cit_id"
								removable
								class="chip"
								@remove="
									profileStore.profileChanges.cities.splice(
										profileStore.profileChanges.cities.findIndex(
											(el) => el.cit_id === city.cit_id,
										),
										1,
									)
								"
							>
								{{ city.cit_name }}
							</p-chip>
							<p-chip
								v-if="profileStore.profileChanges.cities?.length"
								class="chip transparent"
								@click="profileStore.profileChanges.cities = []"
							>
								<i-ic-outline-restart-alt />
								<span>{{ $t("app.base.clear", 2) }}</span>
							</p-chip>
						</div>
					</div>
					<div class="item">
						<p-multi-select
							id="profile-languages"
							v-model="profileStore.languageChangesCodes"
							:options="languages"
							option-label="lng_name"
							:show-toggle-all="false"
							option-value="lng_code"
							:max-selected-labels="0"
							:placeholder="$t('app.teacher.languagesTaught') + ' *'"
							:selected-items-label="$t('app.teacher.languagesTaught') + ' *'"
							fluid
							:pt="{
								root: {
									class: 'my-profile-input-root',
								},
							}"
							@change="handleLanguageSelect"
						></p-multi-select>
						<div class="select-wrapper-chips-wrapper">
							<p-chip
								v-for="lang in profileStore.profileChanges.languages"
								:key="lang.lng_code"
								removable
								:pt="{
									root: {
										class: 'my-profile-chip-root',
									},
								}"
								@remove="
									profileStore.profileChanges.languages.splice(
										profileStore.profileChanges.languages.findIndex(
											(el) => el.lng_code === lang.lng_code,
										),
										1,
									)
								"
							>
								{{ lang.lng_name }}
							</p-chip>
							<p-button
								v-if="profileStore.profileChanges.languages?.length"
								:pt="{
									root: {
										class: ['clear-all-btn'],
									},
								}"
								variant="text"
								@click="profileStore.profileChanges.languages = []"
							>
								<i-ic-outline-restart-alt />
								<span>{{ $t("app.base.clear", 2) }}</span>
							</p-button>
						</div>
						<!-- <div class="chip-wrap">
							<p-chip
								v-for="lang in profileStore.profileChanges.languages"
								:key="lang.lng_code"
								removable
								class="chip"
								@remove="
									profileStore.profileChanges.languages.splice(
										profileStore.profileChanges.languages.findIndex(
											(el) => el.lng_code === lang.lng_code,
										),
										1,
									)
								"
							>
								{{ lang.lng_name }}
							</p-chip>
							<p-chip
								v-if="profileStore.profileChanges.languages?.length"
								class="chip transparent"
								@click="profileStore.profileChanges.languages = []"
							>
								<i-ic-outline-restart-alt />
								<span>{{ $t("app.base.clear", 2) }}</span>
							</p-chip>
						</div> -->
					</div>
				</div>
				<label class="sub-title">
					{{ $t("app.teacher.profile.description") }}
				</label>
				<div class="text-area-wrap">
					<div class="text-area-counter">
						{{ profileStore.profileChanges.usr_description?.length }}
						/255
					</div>
					<p-textarea
						:value="profileStore.profileChanges.usr_description"
						fluid
						@input="handleDescriptionInput"
					></p-textarea>
				</div>
			</p-fluid>
		</div>
	</div>
</template>
<style scoped lang="scss">
.teacher-profile-fake-circle {
	margin-top: 130px;
}
.content-wrap {
	display: flex;
	flex-wrap: wrap;
	gap: 3rem;
	margin: 1rem 0;
	position: relative;

	@media screen and (max-width: $mobile) {
		background: $daidai-light-blue;
		margin: 0;
		padding: 2rem 1rem;
	}
	.image-wrap {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 1rem;

		.general-info-image-fix {
			display: flex;
			align-items: start;

			:deep() {
				img {
					height: unset;
					width: unset;
					max-width: unset;
					max-height: unset;
					min-width: 100%;
					min-height: 100%;
					object-fit: cover;
				}
			}
		}

		@media screen and (max-width: $mobile) {
			background: $daidai-light-blue;
			margin-top: 0;
			padding: 1rem 0.5rem;
			position: absolute;
			left: 50%;
			top: -200px;
			transform: translateX(-50%);
			z-index: 2;
			background-color: transparent;
			label.sub-title {
				margin-top: 0;
				margin-bottom: 0.3rem;
			}
		}
	}
	.basic-wrap {
		flex-grow: 1;
		width: unset;
		max-width: 750px;

		@media screen and (max-width: 1460px) {
			width: 100%;
		}
	}
	div.sub-title {
		margin: 1.25rem 0 2rem;
		display: flex;
		gap: 1rem;
		align-items: center;

		@media screen and (max-width: $mobile) {
			margin-top: 2.5rem;
		}
		label {
			display: block;
			font-weight: 500;
			font-size: 1.125rem;
		}

		.panding-validation-icon {
			// position: absolute;
			// top: 20px;
			// right: 0;
			color: $daidai-yellow;
			// font-size: 40px;
			font-size: 30px;
		}
	}
	.item-group {
		display: flex;
		gap: 1rem;
		justify-content: space-between;
		flex-wrap: wrap;
		width: 100%;
	}
	.item {
		display: flex;
		flex-direction: column;
		gap: 1.5rem;
		width: calc(50% - 1rem);
		margin-bottom: 1rem;

		@media screen and (max-width: $mobile) {
			width: 100%;
		}
	}
	.chip-wrap {
		display: flex;
		gap: 0.5rem;
		flex-wrap: wrap;
		max-width: 100%;
		margin-bottom: 0.75rem;
		.chip {
			background: var(--p-primary-200);
			font-size: 0.875rem;
			padding-block: 0.25rem;
			&.transparent {
				background: transparent;
				color: var(--p-primary-600);
				font-weight: 500;
				font-size: 0.9rem;
				cursor: pointer;
				transition: background-color 0.3s;
				&:hover {
					background: var(--p-primary-50);
				}
			}
			.faico {
				font-size: 1.25rem;
			}
		}
	}
	.text-area-wrap {
		position: relative;

		.text-area-counter {
			position: absolute;
			top: -25px;
			right: 5px;
			font-size: 0.875rem;
			color: var(--p-primary-500);
		}
	}
}
</style>

<style lang="scss">
.general-info-file-upload-fix {
	.p-button-icon {
		display: none;
	}
}
</style>
